import { useMemo } from "react";

import { useQuery } from "@apollo/client";

import { type ApolloResult } from "../../shared/models";
import { timestampToDate } from "../../shared/utils/date";
import { type ReleaseNotesQuery, type ReleaseNotesQueryVariables, getReleaseNotes } from "../api/queries";

interface UseReleaseNotesParams {
    lensId: string;
}

type UseReleaseNotesReturn = ApolloResult<{
    releaseNotes: {
        id: string;
        note: string;
        date: Date | null;
    }[];
}>;

export const useReleaseNotes = ({ lensId }: UseReleaseNotesParams): UseReleaseNotesReturn => {
    const { data, loading, error } = useQuery<ReleaseNotesQuery, ReleaseNotesQueryVariables>(getReleaseNotes, {
        skip: !lensId,
        variables: {
            lensId,
            limit: 5,
        },
    });

    return useMemo(() => {
        return {
            loading,
            error,
            releaseNotes: (data?.releaseNotes.releaseNotes ?? [])
                .filter(
                    (
                        releaseNote,
                    ): releaseNote is typeof releaseNote & {
                        releaseDate: NonNullable<typeof releaseNote.releaseDate>;
                    } => releaseNote.note !== "" && releaseNote.releaseDate !== null,
                )
                .map((releaseNote) => ({
                    id: `${releaseNote.releaseDate.seconds}.${releaseNote.releaseDate.nanos}-${releaseNote.note}`,
                    note: releaseNote.note,
                    date: timestampToDate(releaseNote.releaseDate),
                })),
        };
    }, [data?.releaseNotes.releaseNotes, error, loading]);
};
